import React, {FC, ReactNode, useContext, useEffect, useMemo, useState} from 'react';
import configActions from '../../actions/config.actions';
import getAlert from '../../components/alert/utils/getAlert';
import isAlertVisible from '../../components/alert/utils/isAlertVisible';
import {useDispatch, useSelector} from 'react-redux';
import {AlertComponentType} from '../../components/alert/types';
import {selectAlerts, selectConfig, selectIsLoggedIn} from '../../selectors';

export interface FnGamesTourContextValue {
    open: boolean;
    onShow: () => void;
    onFinish: () => void;
    notifyModalClose: () => void;
}

export interface FnGamesTourProviderProps {
    children: ReactNode;
}

export const FnGamesTourContext = React.createContext<FnGamesTourContextValue>({
    open: false,
    onShow: () => {},
    onFinish: () => {},
    notifyModalClose: () => {}
});

const FnGamesTourProvider: FC<FnGamesTourProviderProps> = ({
    children
}: FnGamesTourProviderProps) => {
    const isFortniteGamesOnboarded = useSelector((state: any) =>
        state.get('config').get('isFortniteGamesOnboarded')
    );

    const props = useSelector((state: any) => ({
        alertsState: selectAlerts(state),
        configState: selectConfig(state),
        isLoggedIn: selectIsLoggedIn(state)
    }));

    const {isLoggedIn, configState, alertsState} = props || {};
    const {enableFnGames} = configState || {};
    const {alerts = [], loading, lastFetch} = alertsState || {};

    const isOtherPopupsVisible = () => {
        const alertProps = {
            ...props,
            affiliateAccount: null,
            taxState: null,
            history: null,
            traceSecurityLink: null
        };

        return alerts
            .filter(alertKey => {
                const {AlertPopupProps} = getAlert(alertKey, alertProps) || {};
                return !!AlertPopupProps;
            })
            .map(alert => isAlertVisible(alert, AlertComponentType.HIDE_POPUP))
            .some(isOpen => isOpen);
    };

    const getIsCanOpen = () => isLoggedIn && enableFnGames && !loading && !isOtherPopupsVisible();

    const [open, setOpen] = useState(() => !isFortniteGamesOnboarded && !!getIsCanOpen());
    const dispatch = useDispatch();

    const onFinish = () => {
        if (!isFortniteGamesOnboarded) {
            dispatch(configActions.setAppConfigFGOnboarding(true));
        }
        setOpen(false);
    };

    const onShow = () => {
        setOpen(true);
    };

    const tryToShowModal = () => {
        if (!isFortniteGamesOnboarded && getIsCanOpen()) {
            setOpen(true);
        }
    };

    useEffect(() => {
        tryToShowModal();
    }, [alerts.length, lastFetch]);

    const value = useMemo(
        () => ({
            open,
            onShow,
            onFinish,
            notifyModalClose: tryToShowModal
        }),
        [open, alertsState]
    );

    return <FnGamesTourContext.Provider value={value}>{children}</FnGamesTourContext.Provider>;
};

export const useFnGamesTour = (): FnGamesTourContextValue => {
    return useContext(FnGamesTourContext);
};

export default FnGamesTourProvider;
